import { type FormEvent, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { api, messageOf, type PolicyDraft, type PolicyResponse } from "../lib/api.ts";
import { useToken } from "../lib/auth.tsx";
import type { AgentOutlet } from "./AgentLayout.tsx";

type FormState = PolicyDraft;

const empty: FormState = {
  agentName: "",
  tone: "",
  allowedTopics: [],
  forbiddenTopics: [],
  guardrails: "",
  fallbackBehavior: "message",
  fallbackMessage: "",
  handoffEmail: null,
  maxAnswerLength: 800,
};

export function Answers() {
  const { agent, mark } = useOutletContext<AgentOutlet>();
  const token = useToken();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(empty);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    void api<{ policies: PolicyResponse[] }>(`/v1/agents/${agent.id}/policies`, token)
      .then((body) => {
        if (!active) return;
        const latest = body.policies[0];
        if (latest) setForm(toForm(latest));
        setReady(true);
      })
      .catch((caught: unknown) => {
        if (active) setError(messageOf(caught));
      });
    return () => {
      active = false;
    };
  }, [agent.id, token]);

  async function onSave(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      await api(`/v1/agents/${agent.id}/policies`, token, {
        method: "POST",
        json: { ...form, handoffEmail: form.handoffEmail?.trim() || null },
      });
      mark("answers");
      navigate("../test");
    } catch (caught) {
      setError(messageOf(caught));
      setPending(false);
    }
  }

  return (
    <form className="card stack" onSubmit={onSave}>
      <header className="step-head">
        <p className="eyebrow">Step 3 of 6</p>
        <h1>Set how it answers</h1>
        <p className="lead">Say how it should sound, what it should refuse, and what to do when the files do not have the answer. Save, then try it in the chat.</p>
      </header>
      {!ready && !error ? <p className="muted">Loading…</p> : null}
      <label>
        Name visitors see
        <input required maxLength={80} value={form.agentName} onChange={(event) => setForm({ ...form, agentName: event.target.value })} />
      </label>
      <label>
        How it should sound
        <textarea required maxLength={280} rows={2} value={form.tone} onChange={(event) => setForm({ ...form, tone: event.target.value })} />
      </label>
      <p className="muted">Warm and short works well. One sentence is enough.</p>
      <label>
        Topics it may answer
        <textarea rows={3} value={form.allowedTopics.join("\n")} placeholder={"Hours\nShipping"} onChange={(event) => setForm({ ...form, allowedTopics: lines(event.target.value) })} />
      </label>
      <p className="muted">One topic per line. Leave this blank to answer anything your files cover.</p>
      <label>
        Topics it should refuse
        <textarea rows={3} value={form.forbiddenTopics.join("\n")} placeholder={"Legal advice\nCompetitor prices"} onChange={(event) => setForm({ ...form, forbiddenTopics: lines(event.target.value) })} />
      </label>
      <label>
        Rules it must follow
        <textarea required maxLength={2000} rows={4} value={form.guardrails} onChange={(event) => setForm({ ...form, guardrails: event.target.value })} />
      </label>
      <fieldset>
        <legend>When the files do not have the answer</legend>
        <label className="choice">
          <input type="radio" name="fallback" checked={form.fallbackBehavior === "message"} onChange={() => setForm({ ...form, fallbackBehavior: "message" })} />
          Show a message
        </label>
        <label className="choice">
          <input type="radio" name="fallback" checked={form.fallbackBehavior === "collect_contact"} onChange={() => setForm({ ...form, fallbackBehavior: "collect_contact" })} />
          Ask for their name and email
        </label>
        <label className="choice">
          <input type="radio" name="fallback" checked={form.fallbackBehavior === "handoff_email"} onChange={() => setForm({ ...form, fallbackBehavior: "handoff_email" })} />
          Save it for your team
        </label>
      </fieldset>
      <label>
        Message to show
        <textarea required maxLength={500} rows={3} value={form.fallbackMessage} onChange={(event) => setForm({ ...form, fallbackMessage: event.target.value })} />
      </label>
      <label>
        Team email
        <input
          type="email"
          required={form.fallbackBehavior === "handoff_email"}
          value={form.handoffEmail ?? ""}
          placeholder="hello@yourbusiness.com"
          onChange={(event) => setForm({ ...form, handoffEmail: event.target.value })}
        />
      </label>
      <p className="muted">Needed when you save questions for your team. The question is kept for you to follow up.</p>
      <label>
        How long answers can be
        <input
          type="number"
          min={80}
          max={4000}
          step={1}
          required
          value={form.maxAnswerLength}
          onChange={(event) => setForm({ ...form, maxAnswerLength: Number(event.target.value) })}
        />
      </label>
      <p className="muted">This is the maximum number of characters. 800 is a good start.</p>
      {error ? <p className="banner bad">{error}</p> : null}
      <button className="button block" type="submit" disabled={pending || !ready}>
        {pending ? "Saving…" : "Save and try it"}
      </button>
    </form>
  );
}

function toForm(policy: PolicyResponse): FormState {
  return {
    agentName: policy.agentName,
    tone: policy.tone,
    allowedTopics: policy.allowedTopics,
    forbiddenTopics: policy.forbiddenTopics,
    guardrails: policy.guardrails,
    fallbackBehavior: policy.fallbackBehavior,
    fallbackMessage: policy.fallbackMessage,
    handoffEmail: policy.handoffEmail,
    maxAnswerLength: policy.maxAnswerLength,
  };
}

function lines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}
